import { Box, VStack, Text, HStack } from '@chakra-ui/react';
import { TransactionError } from '@thirdweb-dev/react';
import React, { FC, useState } from 'react';

import { ExternalLink } from '@app/components/ExternalLink';
import { HeaderControls } from '@app/components/Swap/components/HeaderControls';
import { TokenInput } from '@app/components/Swap/components/TokenInput';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { useAddLiquidity } from '@app/hooks/liquidity/useAddLiquidity';
import { useTokenBalance } from '@app/hooks/swap/useTokenBalance';
import { LiquidityInfo } from '@app/screens/liquidity/AddLiquidity/components/LiquidityInfo/LiquidityInfo';
import { PlusButton } from '@app/screens/liquidity/AddLiquidity/components/PlusButton';
import { SubmitLiquidityButton } from '@app/screens/liquidity/AddLiquidity/components/SubmitLiquidityButton';
import { ViewSelectorButton } from '@app/screens/liquidity/AddLiquidity/components/ViewSelectorButton';
import { useAddLiquidityPageData } from '@app/screens/liquidity/AddLiquidity/hooks';
import { Notifications } from '@app/screens/liquidity/AddLiquidity/Notifications';
import { Token } from '@app/types/token';
import { PoolNotifications } from '@app/screens/liquidity/AddLiquidity/PoolNotifications';

interface Props {
  inModalView?: boolean;
  initialData?: {
    from: {
      token: Token;
      amount: string;
    };
    to: {
      token: Token;
      amount: string;
    };
  };
}

export const AddLiquidity: FC<Props> = ({
  inModalView = false,
  initialData
}) => {
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);
  const [view, setView] = useState<'lp' | 'spnft'>('lp');
  const {
    state,
    handleFromTokenChange,
    handleToTokenChange,
    handleFromTokenSelect,
    handleToTokenSelect,
    resetStateValues,
    pairAddress
  } = useAddLiquidityPageData(initialData);

  const { addLiquidity, loading: addLiquidityInProgress } = useAddLiquidity(
    state.from,
    state.to
  );

  const { data: fromTokenBalance, isLoading: isLoadingFromTokenBalance } =
    useTokenBalance(state.from.token?.contractAddress ?? '');

  const { data: toTokenBalance, isLoading: isLoadingToTokenBalance } =
    useTokenBalance(state.to.token?.contractAddress ?? '');

  return (
    <Box maxWidth="800px" mx="auto" w="100%" mt={3}>
      {!inModalView && (
        <HStack mb="12px">
          <Text
            fontSize="16px"
            fontWeight={400}
            lineHeight="20px"
            color="whiteOpacity.50"
            textAlign="left"
            whiteSpace="nowrap"
          >
            UniV2 / Stable
          </Text>
          <HeaderControls />
        </HStack>
      )}
      <Box
        padding={!inModalView ? '12px' : 0}
        bg="gray.2"
        borderRadius={16}
        w="100%"
      >
        <HStack
          p="8px 10px"
          bg="neutral.1000"
          borderRadius="12px"
          border="1px solid"
          borderColor="black"
          mb="26px"
          display="none"
        >
          <ViewSelectorButton
            disabled
            sub="Coming soon"
            isActive={view === 'spnft'}
            onClick={() => setView('spnft')}
            label="spNFT"
          />
          <ViewSelectorButton
            isActive={view === 'lp'}
            onClick={() => setView('lp')}
            label="LP only"
          />
        </HStack>
        <VStack>
          <TokenInput
            label="Token 1"
            showMax
            source="from"
            values={state.from}
            onChange={handleFromTokenChange}
            disabled={false}
            onTokenSelect={handleFromTokenSelect}
            disableSelect={!!initialData}
          />
          <PlusButton />
          <TokenInput
            label="Token 2"
            showMax
            values={state.to}
            source="to"
            disabled={false}
            onTokenSelect={handleToTokenSelect}
            onChange={handleToTokenChange}
            disableSelect={!!initialData}
          />
          {state.from.token && state.to.token && (
            <LiquidityInfo state={state} pairAddress={pairAddress} />
          )}
          <PoolNotifications pairAddress={pairAddress} />
          <SubmitLiquidityButton
            state={state}
            fromTokenBalance={fromTokenBalance?.displayValue}
            toTokenBalance={toTokenBalance?.displayValue}
            addLiquidityInProgress={addLiquidityInProgress}
            onSubmit={async () => {
              try {
                const res = await addLiquidity();

                if (res['reason'] !== undefined) {
                  showErrorToast(res.reason);
                } else if (res instanceof Error) {
                  showErrorToast(res.message);
                } else {
                  resetStateValues();

                  const txHash = res?.receipt?.transactionHash;

                  showSuccessToast(
                    `Successfully added liquidity ${state.from.token?.symbol}/${state.to.token?.symbol}`,
                    <ExternalLink txHash={txHash} />
                  );

                  setFeedback({
                    status: 'success',
                    title: 'Transaction Confirmed',
                    message: 'Your transaction has been successfully completed'
                  });
                }
              } catch (e) {
                if (e instanceof TransactionError) {
                  showErrorToast(e.reason);
                } else {
                  showErrorToast(
                    `Error adding liquidity ${state.from.token?.symbol}/${state.to.token?.symbol}`
                  );
                }

                setFeedback({
                  status: 'error',
                  title: 'Transaction Error',
                  message:
                    'There was an error processing your transaction. Please check the details and try again'
                });
              }
            }}
            areValuesEmpty={!state.from.amount || !state.to.amount}
            areTokensSelected={!!(state.from.token && state.to.token)}
          />
          <Notifications
            state={state}
            pairAddress={pairAddress}
            fromTokenBalance={fromTokenBalance?.displayValue}
            toTokenBalance={toTokenBalance?.displayValue}
          />
          <TransactionResultModal
            status={feedback?.status}
            title={feedback?.title}
            message={feedback?.message}
            isOpen={!!feedback}
            onClose={() => setFeedback(null)}
          />
        </VStack>
      </Box>
    </Box>
  );
};
